import { trackById, trackByIndex } from 'src/app/shared/utils/track-by.util';
import { isExternalFileUrl, openFileInNewTab } from 'src/app/shared/utils/file-link.util';
import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, Type, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { isSameDay } from 'date-fns';
import { ActivatedRoute } from '@angular/router';
import { EMPTY, forkJoin, Subject, Subscription } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { getEnumLookup, getNamedEnum } from 'src/app/_helpers/enum-helper';
import { commonIsVisible, isVisibleExcept, isVisibleOnly, SampleCondition, SampleTypeCategoryCombos } from 'src/app/_helpers/sample-helper';
import { CustomFieldTypeEnum, HrisTypes } from 'src/app/enums/customFieldType.enum';
import { CustomField } from 'src/app/models/customField.model';
import { ExposureTypicality } from 'src/app/enums/exposureTypicality.enum';
import { MonitoringReasonTypeEnum } from 'src/app/enums/monitoringReasonType.enum';
import { OvertimeFrequencyEnum } from 'src/app/enums/overtimeFrequency.enum';
import { SampleCategoryEnum } from 'src/app/enums/sampleCategory.enum';
import { SampleStatusColors, SampleStatusEnum, getFilteredSampleStatuses, getStatusBadgeClass as getStatusBadgeClassFn } from 'src/app/enums/sampleStatus.enum';
import { SampleTypeEnum } from 'src/app/enums/sampleType.enum';
import { UnsampledTimeCorrectionType } from 'src/app/enums/unsampledTimeCorrectionType.enum';
import { WeatherConditionType } from 'src/app/enums/weatherConditionType.enum';
import { IhLabResult } from 'src/app/models/IhLabResult.model';
import { Lookup } from 'src/app/models/lookup.model';
import { Sample, SampleEndPoint } from 'src/app/models/sample.model';
import { PpeAssignment } from 'src/app/models/ppeAssignment.model';
import { FileAttachment } from 'src/app/models/fileAttachment.model';
import { CustomObjectService } from 'src/app/services/customObject.service';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import { EventService } from 'src/app/services/event.service';
import { OrganizationLevelService } from 'src/app/services/organizationLevel.service';
import { SampleService } from 'src/app/services/sample.service';
import { ReportGenerationService } from 'src/app/services/report-generation.service';
import { ToastService } from 'src/app/services/toast.service';
import { ValueConversionService } from 'src/app/services/value-conversion.service';
import { PdfGeneratedData } from 'src/app/models/pdfGeneratedData.model';
import { EventKeys } from 'src/app/shared/constants/eventKeys.constants';
import { sampleDetailsRouteConfig } from 'src/app/shared/constants/sampleDetailsRouteConfig.constants';
import { RolePermissionObjects } from 'src/app/shared/constants/rolePermissionObjects.constants';
import { ConfirmDialogComponent } from 'src/app/shared/confirmDialog/confirmDialog.component';
import { GridPermissions, gridToPermissionFlags } from 'src/app/shared/grid/core-grid/gridPermissions.enum';
import { PermissionFlags } from 'src/app/enums/permissionFlags.enum';
import { PermissionService } from 'src/app/services/permission.service';
import { ENTITY_TYPES } from 'src/app/shared/widget-registry';
import { FlyoutContainerComponent } from 'src/app/shared/flyout/flyout-container/flyout-container.component';
import { IFormDialogData } from 'src/app/shared/form/form.component';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { SampleShiftCoverage } from 'src/app/enums/sampleShiftType.enum';
import { ShiftType } from 'src/app/enums/shiftTypeValue.enum';
import { SampleDialogComponent } from 'src/app/modals/sample-dialog/sample-dialog.component';
import { VoidReasonDialogComponent } from 'src/app/modals/void-reason-dialog/void-reason-dialog.component';
import { CalDriftWarningDialogComponent, CalDriftWarningResult } from 'src/app/modals/cal-drift-warning-dialog/cal-drift-warning-dialog.component';
import { OutlierReasonDialogComponent } from 'src/app/modals/outlier-reason-dialog/outlier-reason-dialog.component';
import { SampleValidationConfig } from 'src/app/models/sampleValidationConfig.model';
import { CAL_DRIFT_VOID_REASON } from 'src/app/shared/constants/sampleValidationRules.constants';
import { calVariabilityPercent, maxCalDriftPercentFor, shouldWarnCalDrift as shouldWarnCalDriftFor } from 'src/app/shared/utils/cal-drift.util';
import { TenantRiskService } from 'src/app/services/tenantRisk.service';
import { UnlinkCocDialogComponent } from 'src/app/modals/unlink-coc-dialog/unlink-coc-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { Units, UnitSymbols } from 'src/app/enums/units.enum';
import { ParameterTypeEnum } from 'src/app/enums/parameterType.enum';
import { LengthUnit } from 'src/app/enums/unitPreferences.enum';
import { LabRequisitionService } from 'src/app/services/lab-requisition.service';
import { getOelCorrectionMethodNames } from 'src/app/enums/oelCorrectionMethod.enum';
import { DeviceCalibrationHistoryService } from 'src/app/services/deviceCalibrationHistory.service';
import { DocumentType } from 'src/app/enums/documentType.enum';
import { getImgSrc } from 'src/app/_helpers/attachment-helper';
import { EmailStatus } from 'src/app/enums/emailStatus.enum';
import { TranslateService } from 'src/app/services/translate.service';
import { ColumnDetails } from 'src/app/models/columnDetails.model';
import { ReportService } from 'src/app/services/report.service';
import { Report } from 'src/app/models/report.model';
import { ILabResultDialogData, LabResultsDialogComponent } from 'src/app/modals/lab-results-dialog/lab-results-dialog.component';
import { SampleTasks } from 'src/app/models/sampleTasks.model';

export interface KeyValue { property?: string; label: string; value: string | number; isFlag?: boolean; isShown?: boolean; note?: string; link?: string; isPii?: boolean }

@Component({
  standalone: false,
  selector: 'app-sample-details',
  templateUrl: './sample-details.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./sample-details.component.scss']
})
export class SampleDetailsComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(FlyoutContainerComponent) flyout!: FlyoutContainerComponent;

  private destroy$ = new Subject<void>();
  private reportsSubscription?: Subscription;
  sampleDetails: Sample;
  ppeAssignments: PpeAssignment[] = [];
  overviewFields: KeyValue[];
  equipmentFields: KeyValue[];
  calibrationFields: KeyValue[];
  metricFields: KeyValue[];
  employeeFields: KeyValue[] = [];
  customObjectsList: CustomField[] = [];
  tabs: { routerLink: string; title: string; }[];
  labFields: KeyValue[] = [];
  controlFields: KeyValue[] = [];
  healthFields: KeyValue[] = [];
  exposureTypicality: string;
  displayedColumns: string[];
  organizationsList: Lookup[] = [];
  sampleStatusEnum = SampleStatusEnum;
  sampleStatusList: ReturnType<typeof getEnumLookup> = getEnumLookup(SampleStatusEnum);
  sampleStatusColors = SampleStatusColors;
  readonly permissionObject = RolePermissionObjects.Sample;
  readonly editAction = GridPermissions.Update;

  /** Rebuild sampleStatusList based on sampleType / sampleCategory, preserving the current persisted status */
  private buildFilteredStatusList(): void {
    this.sampleStatusList = getFilteredSampleStatuses(
      getEnumLookup(SampleStatusEnum),
      this.sampleDetails?.sampleType,
      this.sampleDetails?.sampleCategory,
      this.sampleDetails?.sampleStatus
    );
  }

  getStatusBadgeClass = getStatusBadgeClassFn;
  weatherConditionEnum = getNamedEnum(WeatherConditionType);
  sampleStatus: number;
  isLoading = true;
  showReportGenerationPopup = false;
  isGeneratingReport = false;
  temperatureUnit: string;
  sampleTypeEnum = SampleTypeEnum;
  locationFields: KeyValue[];
  emailStatusEnum = EmailStatus;
  rawSampleData: Sample;
  reportGenerationDisabledMessage: string;
  columnDetails: { [property: string]: ColumnDetails };
  contextCollapsed = false;
  sampleCategoryEnum = SampleCategoryEnum;

  // PII anonymisation (G5) — masking is server-driven via EndPoint['PiiMasked'];
  // the FE only decides how to present what the server sent.
  piiMasked = false;
  piiRevealed = false;
  hasPiiFields = false;
  locationIsPii = false;

  // Precomputed hasValue booleans — updated in sampleDetailsConversions + populateSampleDetailsFields
  hasSampleMediaName = false;
  hasTemperatureMinC = false;
  hasTemperatureMaxC = false;
  hasHumidityPercent = false;
  hasPressureMmhg = false;
  hasVoidReason = false;
  hasSummaryNote = false;
  hasProcessName = false;
  hasVisibleCalibrationFields = false;
  hasVisibleLabFields = false;
  hasVisibleEquipmentFields = false;
  hasVisibleLocationFields = false;
  // Whether the Location section applies to this sample category at all (any location field is shown).
  // Drives whether the entire Location card renders — Location is an Area-level attribute and must not
  // appear (not even as an empty "No location information" card) for Air Personal samples (DASH-2980).
  hasApplicableLocationFields = false;
  sampleReports: Report[] = [];
  visibleReports: Report[] = [];
  readonly maxInlineReports = 2;
  readonly piiPermissionObject = RolePermissionObjects.Pii;
  readonly readAction = GridPermissions.Read;
  loadingReports = false;
  taskTableColumns: string[] = [
    nameOf<SampleTasks>(x => x.taskGroupName),
    nameOf<SampleTasks>(x => x.taskName),
    nameOf<SampleTasks>(x => x.approximateTimeMinutes),
    nameOf<SampleTasks>(x => x.applicablePpeWorn)
  ];

  constructor(private sampleService: SampleService,
    private activatedRoute: ActivatedRoute,
    private toastService: ToastService,
    private dateFormatService: DateFormatService,
    private customObjectService: CustomObjectService,
    private eventService: EventService,
    private valueConversionService: ValueConversionService,
    private changeDetectorRef: ChangeDetectorRef,
    private organizationLevelService: OrganizationLevelService,
    private reportGenerationService: ReportGenerationService,
    private dialog: MatDialog,
    private labRequisitionService: LabRequisitionService,
    private deviceCalibrationHistoryService: DeviceCalibrationHistoryService,
    private translateService: TranslateService,
    private reportService: ReportService,
    private permissionService: PermissionService,
    private tenantRiskService: TenantRiskService
  ) { }

  /** Live per-tenant sample acceptance rules (DASH-2620/DASH-2621); null until loaded. */
  private sampleValidationConfig: SampleValidationConfig | null = null;

  ngOnInit(): void {
    const sampleId = this.activatedRoute.snapshot.params.id;
    this.getSampleDetails(sampleId);
    this.loadSampleValidationConfig();
    this.tabs = sampleDetailsRouteConfig(sampleId.toString());
    this.temperatureUnit = this.valueConversionService.getParameterUnit(ParameterTypeEnum.Temperature);
    const approvedStatus = this.translateService.getDynamicTranslation('Approved');
    const voidStatus = this.translateService.getDynamicTranslation('Void');
    this.reportGenerationDisabledMessage = this.translateService.getDynamicTranslation(
      'Report generation is disabled unless sample status is {{status}}',
      { status: `${approvedStatus}/${voidStatus}` }
    );
  }

  ngAfterViewInit(): void {
    this.eventService.on<Event>(EventKeys.flyoutClose).pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (this.flyout?.close) {
        this.getSampleDetails(this.sampleDetails.id);
        this.flyout.close();
      }
    })
  }

  getSampleDetails(sampleId: string) {
    this.isLoading = true;
    forkJoin({
      organizationsList: this.organizationLevelService.getAll(),
      sampleDetails: this.sampleService.getSampleById(sampleId),
      columnDetails: this.sampleService.getFieldConfiguration()
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: ({ organizationsList, sampleDetails, columnDetails }) => {
        // The BE sets bag key "PiiMasked", but SampleModel.EndPoint is a PropertyBag<object>
        // whose keys are camelCased on the wire (PropertyBagConverter) -> "piiMasked".
        this.piiMasked = sampleDetails.endPoint?.['piiMasked'] === true;
        this.piiRevealed = false;
        this.hasPiiFields = false;
        const dimensions = sampleDetails.ihLabResults.find(result => result?.wipeDimensions)?.wipeDimensions;
        this.rawSampleData = { ...sampleDetails, wipeDimensions: dimensions };
        this.organizationsList = organizationsList;
        sampleDetails.ihLabResults = this.getUpdatedSampleResults(sampleDetails);
        this.columnDetails = columnDetails;
        this.sampleDetailsConversions(sampleDetails);
        this.sampleStatus = sampleDetails.sampleStatus;
        this.exposureTypicality = getNamedEnum(ExposureTypicality)[sampleDetails.exposureTypicality] || '-';
        this.populateSampleDetailsFields(sampleDetails);
        if (sampleDetails.endPoint && sampleDetails.endPoint.entityType === "Employee") {
          this.loadEmployeeFields(sampleDetails.endPoint);
        }
        this.loadSampleReports(sampleId);
        this.isLoading = false;
      }, error: (error) => {
        this.handleError(error);
        this.isLoading = false;
      }
    });
  }

  updateColumnDetails(fields: KeyValue[]): KeyValue[] {
    if (!Array.isArray(fields)) return [];

    return fields.map(field => {
      if (!field.property) return field;
      const config = this.columnDetails?.[field.property];
      const configVisible =
        typeof config?.isVisibleOnDialog === 'boolean' ? config.isVisibleOnDialog : true;
      return {
        ...field,
        label: config?.displayName ?? field.label,
        isShown: (field.isShown ?? true) && configVisible
      };
    });
  }

  getUpdatedSampleResults(sampleDetails: Sample): IhLabResult[] {
    return (sampleDetails?.ihLabResults ?? [])
      .map(res => ({
        ...res,
        applyShiftCorrection: sampleDetails.applyShiftCorrection,
        oelCorrectionMethodName: getOelCorrectionMethodNames()[res.oelCorrectionMethod] || '-',
        oelValue: this.getOelValue(res.oel, res),
        correctedOELValue: this.getOelValue(res.correctedOEL, res),
      }))
      .filter(result => !result.isDeleted).sort((a, b) => (a.agentName || '').localeCompare(b.agentName || ''));
  }

  getOelValue(value, result: IhLabResult) {
    if (value == null) return '-';
    const hasUnit = result.agentUnits != null;
    const val = result.agentUnits != null
      ? this.valueConversionService.getParameterValueAsPerAgentUnit(value, result.agentUnits)
      : value;
    return `${+val.toFixed(4)}${hasUnit ? ' ' + UnitSymbols[result.agentUnits] : ''}`;
  }

  sampleDetailsConversions(sampleDetails) {
    const getTempValue = (value: number | null) => value != null ? Math.round(value * 10) / 10 : null;
    const attachments = sampleDetails.fileAttachments.filter(fa => ![DocumentType.LabResult, DocumentType.Letters, DocumentType.Report].includes(fa.documentType))
      .map(res => ({ ...res, imgSrc: getImgSrc(res?.fileType) }));

    this.sampleDetails = {
      ...sampleDetails, fileAttachments: attachments,
      temperatureMinC: getTempValue(sampleDetails.temperatureMinC),
      temperatureMaxC: getTempValue(sampleDetails.temperatureMaxC),
      unsampledTimeCorrectionName: getNamedEnum(UnsampledTimeCorrectionType)[sampleDetails.unsampledTimeCorrection] || '-'
    };
    this.ppeAssignments = (this.sampleDetails.ppeAssignments ?? []) as PpeAssignment[];
    this.updatePrecomputedFlags();
    this.buildFilteredStatusList();
    this.setCOCUrl();
  }

  loadEmployeeFields(employee: SampleEndPoint): void {
    if (!employee) return;

    this.customObjectService.getAll().pipe(
      switchMap(typeLookups => {
        const employeeType = typeLookups.find(t => t.name === 'Employee');
        if (!employeeType) return EMPTY;
        return this.customObjectService.getCustomObjectFields(employeeType.id);
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: fields => {
        this.customObjectsList = fields.filter(f => this.isCustomFieldKey(f.property));
        this.mapEmployeeFields(employee);
      },
      error: this.handleError
    });
  }

  private handleError = (error: any): void => {
    this.toastService.displayErrorMessage(error);
  }

  isCustomFieldKey(property: string): boolean {
    return property && property.startsWith('f_');
  }

  mapEmployeeFields(employee: SampleEndPoint): void {
    // Identity fields from the endpoint object (live employee data)
    const identityTypes: CustomFieldTypeEnum[] = [
      CustomFieldTypeEnum.HrisPersonId,
      CustomFieldTypeEnum.HrisFirstName,
      CustomFieldTypeEnum.HrisLastName,
      CustomFieldTypeEnum.HrisEmailId,
    ];
    const identityFields = this.customObjectsList
      .filter(f => identityTypes.includes(f.type))
      .map(field => ({ property: field.property, label: field.name, value: employee[field.property] ?? '-', isPii: field.isPii === true }));

    // Snapshot fields from the sample object (job position, title, etc. — captured at time of sampling)
    // Exclude ALL HRIS types — those come from the live endpoint, not the sample snapshot
    const snapshotFields = this.customObjectsList
      .filter(f => !HrisTypes.includes(f.type))
      .map(field => ({ property: field.property, label: field.name, value: this.sampleDetails[field.property] ?? '-', isPii: field.isPii === true }));

    const data = this.sampleDetails;
    this.employeeFields = [
      ...identityFields,
      ...snapshotFields,
      { property: nameOf<Sample>(x => x.organizationLevelName), label: 'Organisational Level', value: this.organizationsList.find(res => res.id === data.endpointOrganizationLevelId)?.name },
      { property: nameOf<Sample>(x => x.shiftLength), label: 'Shift Length', value: data.shiftLength ? `${this.valueConversionService.convertSecondsToHours(data.shiftLength)} hours` : '-' },
      { property: nameOf<Sample>(x => x.averageWeeklyHours_Seconds), label: 'Avg Weekly Hours', value: data.averageWeeklyHours_Seconds ? `${this.valueConversionService.convertSecondsToHours(data.averageWeeklyHours_Seconds)} hours` : '-' },
      { property: nameOf<Sample>(x => x.shiftRoster), label: 'Shift Roster', value: data.shiftRoster != null ? getNamedEnum(ShiftType)[data.shiftRoster]?.replace(/_/g, ' ') ?? '-' : '-' },
    ];
    this.employeeFields = this.updateColumnDetails(this.employeeFields);
    // locationIsPii (Employee endpoint) is set in populateSampleDetailsFields() which runs before this,
    // so an Employee location name is masked client-side even when no custom field is flagged isPii —
    // include it here so the permitted user still gets a Reveal control.
    this.hasPiiFields = this.employeeFields.some(f => f.isPii) || this.locationIsPii;
    this.changeDetectorRef.detectChanges();
  }

  toggleReveal(): void {
    this.piiRevealed = !this.piiRevealed;
  }

  populateSampleDetailsFields(data: Sample): void {
    const valueString = (value: number, type: ParameterTypeEnum) => {
      const unit = this.valueConversionService.getParameterUnit(type);
      return value ? `${parseFloat(value.toFixed(4)).toString()} ${unit}` : '-';
    }

    const getName = (enumObj: Record<string, string | number>, value: number | null | undefined) => (value != null) ? getNamedEnum(enumObj)[value] : '-';

    const { airPersonal, airArea, airBlank, noisePersonal, noiseArea, wipeArea, wipeBlank, wipePersonal, biologicalPersonal, psychologicalPersonal } = SampleTypeCategoryCombos;

    const sectionVisibility = (fields: KeyValue[], visibility: SampleCondition): KeyValue[] => {
      return fields.map(field => ({
        ...field, isShown: (visibility(data) && (field.isShown ?? true))
      }));
    };

    // Hero header already shows: Sample #, Type, Category, Survey, Alt #, SEG, Org Level, Date, Duration
    // So overviewFields only includes fields NOT in hero
    this.overviewFields = [
      { property: nameOf<Sample>(x => x.blankSampleId), label: 'Blank Sample Id', value: data.blankSampleNumber, isShown: isVisibleOnly([airPersonal, airArea])(data) },
      { property: nameOf<Sample>(x => x.managingHygienistId), label: 'Managing Hygienist', value: data.managingHygienistUserName || '-', isShown: isVisibleExcept([airBlank])(data) },
      { property: nameOf<Sample>(x => x.sampledByUserId), label: 'Sampled by', value: data.sampledByUsername || '-', isShown: isVisibleExcept([airBlank])(data) },
      { property: nameOf<Sample>(x => x.shiftType), label: 'Coverage', value: data.shiftType != null ? getNamedEnum(SampleShiftCoverage)[data.shiftType] : '-', isShown: isVisibleExcept([wipeArea, wipeBlank, airBlank, biologicalPersonal])(data) },
      { property: nameOf<Sample>(x => x.monitoringReason), label: 'Monitoring Reason', value: getName(MonitoringReasonTypeEnum, data.monitoringReason), isShown: isVisibleExcept([airBlank])(data) },
      { property: nameOf<Sample>(x => x.mediaLotNumber), label: 'Media Lot Number', value: data.mediaLotNumber || '-', isShown: isVisibleExcept([noisePersonal, wipeArea, wipeBlank, wipePersonal, biologicalPersonal])(data) },
      { property: nameOf<Sample>(x => x.wipeDimensions), label: 'Wipe Dimensions', value: this.formatWipeDimensions(data.wipeDimensions ?? data.ihLabResults?.find(r => r?.wipeDimensions)?.wipeDimensions), isShown: isVisibleOnly([wipeArea, wipeBlank, wipePersonal])(data) },
      { property: nameOf<Sample>(x => x.outlierReason), label: 'Outlier Reason', value: data.outlierReason || '-', isShown: !!data.isOutlier },
    ];

    const isAirOrNoise = isVisibleOnly([airPersonal, airArea, noisePersonal, noiseArea])(data);
    const isWipe = isVisibleOnly([wipeArea, wipeBlank, wipePersonal])(data);
    this.equipmentFields = sectionVisibility([
      { property: nameOf<Sample>(x => x.sensorId), label: 'Sensor', value: data.sensorId ? data.sensorName : '-', isShown: isAirOrNoise },
      { property: nameOf<Sample>(x => x.samplingEquipmentId), label: 'Pump Model', value: data.samplingEquipmentModelNumber || '-', isShown: isAirOrNoise },
      { property: nameOf<Sample>(x => x.samplingEquipmentSerialNumber), label: 'Pump Serial Number', value: data.samplingEquipmentSerialNumber || '-', isShown: isAirOrNoise },
      { property: nameOf<Sample>(x => x.sampleMediaId), label: 'Sampling Media', value: data.sampleMediaName || '-', isShown: true },
      { property: nameOf<Sample>(x => x.mediaLotNumber), label: 'Media Lot Number', value: data.mediaLotNumber || '-', isShown: isWipe }
    ], commonIsVisible);

    const getDate = (date) => this.dateFormatService.utcToLocalDateAndTime(date);

    this.calibrationFields = sectionVisibility([
      { property: nameOf<Sample>(x => x.calibrationEquipmentId), label: 'Calibrator Serial Number', value: data.calibrationEquipmentSerialNumber || '-', isShown: true, link: data.calibrationEquipmentId ? `/dashboard/calibration-details/${data.calibrationEquipmentId}` : null },
      { property: nameOf<Sample>(x => x.calibrationEquipmentModelNumber), label: 'Calibrator Model', value: data.calibrationEquipmentModelNumber || '-', isShown: true },
      { property: nameOf<Sample>(x => x.lastCalibrated), label: 'Last Calibration Date', value: data.lastCalibrated ? getDate(data.lastCalibrated).date : '-', isShown: true },
      { property: nameOf<Sample>(x => x.calibrationDue), label: 'Next Calibration Date', value: data.calibrationDue ? getDate(data.calibrationDue).date : '-', isFlag: data.calibrationOverdueFlag, isShown: true }
    ], isVisibleOnly([airPersonal, airArea, noisePersonal, noiseArea]));

    this.locationIsPii = data.endPoint?.entityType === 'Employee';
    // Location is an Area-level attribute; it is not meaningful for Air Personal samples (DASH-2980),
    // so airPersonal is intentionally excluded from the location section's visibility set.
    this.locationFields = sectionVisibility([
      { label: 'Location', value: data.endPoint?.name ?? '-', isPii: this.locationIsPii },
      { property: nameOf<Sample>(x => x.locationComments), label: 'Location Comments', value: data.locationComments ?? '-' },
    ], isVisibleOnly([airArea, wipeArea, wipeBlank, noisePersonal]));

    const isNextDay = () => {
      return !isSameDay(new Date(data.startDate), new Date(data.endDate));
    };
    const isNoise = data?.sampleType && data?.sampleType === SampleTypeEnum.Noise;

    // Hero header already shows Date and Duration, so metricFields starts with Start/Stop times
    this.metricFields = sectionVisibility([
      { label: 'Start Time', value: data.startDate ? this.dateFormatService.convertUTCDateStringToUserTimeZoneString(data.startDate as string, this.dateFormatService.userTimeFormat) : '-', isShown: isVisibleOnly([airPersonal, airArea, noisePersonal])(data) },
      {
        property: nameOf<Sample>(x => x.endDate),
        label: 'Stop Time',
        value: data.endDate ? this.dateFormatService.convertUTCDateStringToUserTimeZoneString(data.endDate as string, this.dateFormatService.userTimeFormat) : '-',
        isShown: isVisibleOnly([airPersonal, airArea, noisePersonal])(data),
        note: isNextDay() ? '(+1 day)' : ''
      },
      { property: nameOf<Sample>(x => x.sampleAverageCal), label: 'Avg Flow Rate', value: valueString(data.sampleAverageCal, ParameterTypeEnum.FlowRate) || '-', isShown: data.sampleCategory !== SampleCategoryEnum.Blank && !isNoise },
      {
        property: nameOf<Sample>(x => x.prePostCalVariabilityFlag),
        label: '% Difference Between Initial and Final Flowrate',
        value: (data.samplePreCal && data.samplePostCal)
          ? (Math.abs((data.samplePostCal - data.samplePreCal) / data.samplePreCal) * 100).toFixed(2) + '%'
          : '-',
        isFlag: data.prePostCalVariabilityFlag,
        isShown: data.sampleCategory !== SampleCategoryEnum.Blank && !isNoise
      },
      { property: nameOf<Sample>(x => x.samplePreCal), label: isNoise ? 'Pre Calibration Value' : 'Initial Flow Rate', value: data.samplePreCal ? isNoise ? `${data.samplePreCal} ${UnitSymbols[Units.Db]}` : valueString(data.samplePreCal, ParameterTypeEnum.FlowRate) : '-', isShown: data.sampleCategory !== SampleCategoryEnum.Blank },
      { property: nameOf<Sample>(x => x.samplePostCal), label: isNoise ? 'Post Calibration Value' : 'Final Flow Rate', value: data.samplePostCal ? isNoise ? `${data.samplePostCal} ${UnitSymbols[Units.Db]}` : valueString(data.samplePostCal, ParameterTypeEnum.FlowRate) : '-', isShown: data.sampleCategory !== SampleCategoryEnum.Blank },
      {
        property: nameOf<Sample>(x => x.correctedVolume),
        label: 'Corrected Volume', isShown: data.sampleCategory !== SampleCategoryEnum.Blank && !isNoise,
        value: data.correctedVolume ? `${data.correctedVolume} ${this.valueConversionService.getVolumeUnit()}` : '-'
      },
      {
        property: nameOf<Sample>(x => x.sampleVolume),
        label: 'Sample Volume', isShown: data.sampleCategory !== SampleCategoryEnum.Blank && !isNoise,
        value: data.sampleVolume ? `${data.sampleVolume} ${this.valueConversionService.getVolumeUnit()}` : '-'
      }
    ], isVisibleOnly([airPersonal, airArea, noisePersonal]));

    this.labFields = [
      { property: nameOf<Sample>(x => x.labRequisition?.labRequisitionNumber), label: 'Lab Requisition ID', value: data.labRequisition?.labRequisitionNumber ?? '-', isShown: true },
      { property: nameOf<Sample>(x => x.labRequisition?.dateCreated), label: 'Date Shipped', value: data.labRequisition?.dateCreated ? this.dateFormatService.convertUTCDateStringToUserTimeZoneString(data.labRequisition?.dateCreated, this.dateFormatService.userDateFormat) : '', isShown: true },
      { property: nameOf<Sample>(x => x.labRequisition?.labName), label: 'Lab Name', value: data.labRequisition?.labName, isShown: true }
    ];

    // Process is rendered directly in template; controlFields triggers card visibility
    this.controlFields = [
      { property: nameOf<Sample>(x => x.processName), label: 'Process', value: data.processName ?? '', isShown: true },
    ];

    this.healthFields = [
      { property: nameOf<Sample>(x => x.exposureTypicality), label: 'Exposure Typicality', value: getName(ExposureTypicality, data.exposureTypicality), isShown: isVisibleExcept([airBlank])(data) },
      { property: nameOf<Sample>(x => x.exposureAtypicalComments), label: 'Exposure Atypical Comments', value: data.exposureAtypicalComments || '-', isShown: isVisibleExcept([airBlank])(data) && (data.exposureTypicality === ExposureTypicality.AtypicallyHigh || data.exposureTypicality === ExposureTypicality.AtypicallyLow) },
      { property: nameOf<Sample>(x => x.symptoms), label: 'Symptoms Reported', value: data.symptoms ? 'Yes' : 'No', isShown: isVisibleOnly([airPersonal, noisePersonal, wipePersonal, biologicalPersonal, psychologicalPersonal])(data) },
      { property: nameOf<Sample>(x => x.symptomComments), label: 'Symptom Comments', value: data.symptomComments || '-', isShown: isVisibleOnly([airPersonal, noisePersonal, wipePersonal, biologicalPersonal, psychologicalPersonal])(data) && !!data.symptoms },
      { property: nameOf<Sample>(x => x.overtimeFrequency), label: 'Overtime Frequency', value: getName(OvertimeFrequencyEnum, data.overtimeFrequency), isShown: isVisibleOnly([airPersonal, noisePersonal, wipePersonal, biologicalPersonal, psychologicalPersonal])(data) },
    ];

    this.filterHiddenFields();

    this.displayedColumns = [
      nameOf<IhLabResult>(x => x.agentName),
      nameOf<IhLabResult>(x => x.agentGroupName),
      nameOf<IhLabResult>(x => x.jurisdictionName),
      nameOf<IhLabResult>(x => x.oelTypeId),
      nameOf<IhLabResult>(x => x.oel),
      nameOf<IhLabResult>(x => x.correctedOEL),
      nameOf<Sample>(x => x.applyShiftCorrection),
      nameOf<IhLabResult>(x => x.applyPressureCorrection),
      nameOf<IhLabResult>(x => x.oelCorrectionMethod),
      nameOf<Sample>(x => x.analyticalMethodId),
    ];

    this.overviewFields = this.updateColumnDetails(this.overviewFields);
    this.equipmentFields = this.updateColumnDetails(this.equipmentFields);
    this.calibrationFields = this.updateColumnDetails(this.calibrationFields);
    this.metricFields = this.updateColumnDetails(this.metricFields);
    this.labFields = this.updateColumnDetails(this.labFields);
    this.controlFields = this.updateColumnDetails(this.controlFields);
    this.healthFields = this.updateColumnDetails(this.healthFields);
    this.locationFields = this.updateColumnDetails(this.locationFields);
    this.updatePrecomputedFlags();
  }

  private updatePrecomputedFlags(): void {
    const d = this.sampleDetails;
    if (!d) return;
    this.hasSampleMediaName = this.hasValue(d.sampleMediaName);
    this.hasTemperatureMinC = this.hasValue(d.temperatureMinC);
    this.hasTemperatureMaxC = this.hasValue(d.temperatureMaxC);
    this.hasHumidityPercent = this.hasValue(d.humidityPercent);
    this.hasPressureMmhg = this.hasValue(d.pressureMmhg);
    this.hasVoidReason = this.hasValue(d.voidReason);
    this.hasSummaryNote = this.hasValue(d.summaryNote);
    this.hasProcessName = this.hasValue(d.processName);
    this.hasVisibleCalibrationFields = this.hasVisibleFields(this.calibrationFields);
    this.hasVisibleLabFields = this.hasVisibleFields(this.labFields);
    this.hasVisibleEquipmentFields = this.hasVisibleFields(this.equipmentFields);
    this.hasVisibleLocationFields = this.hasVisibleFields(this.locationFields);
    // Section applicability is driven purely by category (isShown), independent of whether a value
    // is present — so applicable-but-empty categories (e.g. Area with no endpoint) still render the
    // "No location information" card, while non-applicable categories (Air Personal) hide it entirely.
    this.hasApplicableLocationFields = !!this.locationFields?.some(f => f.isShown);
  }

  formatWipeDimensions(wipeDimensions: string): string {
    if (!wipeDimensions) return '-';
    try {
      const dims = JSON.parse(wipeDimensions);
      if (dims && typeof dims === 'object') {
        const rawUnit = dims.unit !== undefined ? Number(dims.unit) : NaN;
        const unit = Number.isFinite(rawUnit) ? rawUnit : LengthUnit.cm;
        const lengthSuffix = unit === LengthUnit.ft ? 'ft' : 'cm';
        const areaSuffix = unit === LengthUnit.ft ? 'ft²' : 'cm²';
        const parts = [];
        if (dims.length != null) parts.push(`L: ${dims.length} ${lengthSuffix}`);
        if (dims.width != null) parts.push(`W: ${dims.width} ${lengthSuffix}`);
        if (dims.area != null) parts.push(`Area: ${dims.area} ${areaSuffix}`);
        return parts.length ? parts.join(', ') : '-';
      }
    } catch (error) {
      console.error('Failed to parse wipeDimensions:', error);
    }
    return wipeDimensions;
  }

  setCOCUrl() {
    if (this.sampleDetails.labRequisitionId) {
      this.labRequisitionService.getById(this.sampleDetails.labRequisitionId).pipe(takeUntil(this.destroy$)).subscribe({
        next: labRequisition => {
          if (this.sampleDetails.labRequisition) {
            this.sampleDetails.labRequisition.fileAttachments = labRequisition.fileAttachments ?? [];
          }
        },
        error: (error) => {
          this.toastService.showError(error);
        }
      })
    }
  }

  viewCOC() {
    const validAttachments = this.getValidAttachments(this.sampleDetails.labRequisition?.fileAttachments);
    if (!validAttachments.length) {
      this.toastService.displayErrorMessage('PDF not found', true);
      return;
    }

    this.openFirstAttachment(validAttachments, 'Invalid PDF URL');
  }

  unlinkCOC(): void {
    const dialogRef = this.dialog.open(UnlinkCocDialogComponent, {
      data: {
        sampleNumber: this.sampleDetails.sampleNumber,
        labReqNumber: this.sampleDetails.labRequisition?.labRequisitionNumber,
        sampleId: this.sampleDetails.id
      },
      width: '500px',
      disableClose: true
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
      if (result) {
        this.getSampleDetails(this.sampleDetails.id);
      }
    });
  }

  filterHiddenFields() {
    [this.overviewFields, this.equipmentFields, this.calibrationFields, this.metricFields, this.labFields]
      .forEach(list => {
        if (!Array.isArray(list)) return;
        list.splice(0, list.length, ...list.filter(f => f.isShown));
      });
  }

  openSampleDialog(): void {
    const sampleModel: IFormDialogData = { id: this.sampleDetails.id, columnDetails: this.columnDetails };
    this.openFlyoutInternal(SampleDialogComponent, sampleModel, '90vw');
  }

  openLabResultsDialog(): void {
    const data: ILabResultDialogData = {
      id: this.sampleDetails.id,
      sampleType: this.sampleDetails.sampleType
    };
    const dialogRef = this.dialog.open(LabResultsDialogComponent, {
      data,
      width: '96%',
      disableClose: true
    });
    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result: unknown) => {
      if (result) {
        this.getSampleDetails(this.sampleDetails.id);
      }
    });
  }

  private loadSampleValidationConfig(): void {
    this.tenantRiskService.getCurrent().pipe(takeUntil(this.destroy$)).subscribe({
      next: (tenantRisk) => {
        this.sampleValidationConfig = tenantRisk?.sampleValidationConfig ?? null;
      },
      error: () => {
        this.sampleValidationConfig = null;
      }
    });
  }

  /** Cal variability (%) computed from pre/post cal, mirroring the value shown in the metrics section. */
  private getCalVariabilityPercent(): number | null {
    return calVariabilityPercent(this.sampleDetails?.samplePreCal, this.sampleDetails?.samplePostCal);
  }

  /** Configured max cal drift (%) for the sample type; null when no rule is set. Never falls back to the platform default. */
  private getMaxCalDriftPercent(): number | null {
    return maxCalDriftPercentFor(this.sampleValidationConfig, this.sampleDetails?.sampleType);
  }

  private shouldWarnCalDrift(targetStatus: SampleStatusEnum): boolean {
    return shouldWarnCalDriftFor({
      currentStatus: this.sampleDetails?.sampleStatus,
      targetStatus,
      preCal: this.sampleDetails?.samplePreCal,
      postCal: this.sampleDetails?.samplePostCal,
      maxCalDriftPercent: this.getMaxCalDriftPercent()
    });
  }

  private showCalDriftWarning(): void {
    const dialogRef = this.dialog.open(CalDriftWarningDialogComponent, {
      data: {
        calVariabilityPercent: this.getCalVariabilityPercent(),
        maxCalDriftPercent: this.getMaxCalDriftPercent()
      },
      width: '450px',
    });
    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result: CalDriftWarningResult | undefined) => {
      if (result === CalDriftWarningResult.Continue) {
        this.onStatusChange(false, true);
      } else if (result === CalDriftWarningResult.Void) {
        this.sampleStatus = SampleStatusEnum.Void;
        this.voidSampleDialog(this.translateService.getDynamicTranslation(CAL_DRIFT_VOID_REASON));
      } else {
        this.sampleStatus = this.sampleDetails.sampleStatus;
      }
    });
  }

  voidSampleDialog(prefillReason?: string): void {
    const dialogRef = this.dialog.open(VoidReasonDialogComponent, {
      data: { voidReason: prefillReason ?? this.sampleDetails.voidReason },
      width: '400px',
    });
    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result: string | { confirmed?: boolean; voidReason?: string }) => {
      const voidReason = typeof result === 'string'
        ? result
        : result?.confirmed
          ? (result.voidReason || '')
          : '';

      if (voidReason) {
        this.sampleDetails.voidReason = voidReason;
        this.updatePrecomputedFlags();
        this.onStatusChange(true);
      } else {
        this.sampleStatus = this.sampleDetails.sampleStatus;
      }
    });
  }

  markAsOutlierDialog(): void {
    const dialogRef = this.dialog.open(OutlierReasonDialogComponent, {
      data: { outlierReason: this.sampleDetails.outlierReason || '' },
      width: '400px',
    });
    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((reason: string) => {
      if (reason) {
        const previous = {
          isOutlier: this.sampleDetails.isOutlier,
          outlierReason: this.sampleDetails.outlierReason
        };
        this.sampleDetails.outlierReason = reason;
        this.sampleDetails.isOutlier = true;
        this.saveSampleOutlierStatus(true, previous);
      }
    });
  }

  unmarkAsOutlier(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.getDynamicTranslation('Remove Outlier'),
        message: this.translateService.getDynamicTranslation('Do you want to remove the outlier flag from this sample?'),
        primaryButtonText: this.translateService.getDynamicTranslation('Yes'),
        secondaryButtonText: this.translateService.getDynamicTranslation('Cancel')
      }
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((confirmed: boolean) => {
      if (!confirmed) {
        return;
      }

      const previous = {
        isOutlier: this.sampleDetails.isOutlier,
        outlierReason: this.sampleDetails.outlierReason
      };
      this.sampleDetails.isOutlier = false;
      this.sampleDetails.outlierReason = '';
      this.saveSampleOutlierStatus(false, previous);
    });
  }

  private saveSampleOutlierStatus(
    isMarking: boolean,
    previous?: Pick<Sample, 'isOutlier' | 'outlierReason'>
  ): void {
    this.sampleService.patch(this.sampleDetails.id, {
      isOutlier: this.sampleDetails.isOutlier,
      outlierReason: this.sampleDetails.outlierReason
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.getSampleDetails(this.sampleDetails.id);
        const successMessage = isMarking ? 'Sample marked as outlier successfully' : 'Outlier removed successfully';
        this.toastService.showSuccess(this.translateService.getDynamicTranslation(successMessage), true);
      },
      error: (error) => {
        if (previous) {
          this.sampleDetails.isOutlier = previous.isOutlier;
          this.sampleDetails.outlierReason = previous.outlierReason;
        }
        this.toastService.showError(error);
      }
    });
  }

  protected openFlyoutInternal<TComponent>(component: Type<TComponent>, data?: unknown, width: string = '80vw', closesOnOutsideClick: boolean = true): void {
    if (this.flyout) {
      this.flyout.open(component, data, width, closesOnOutsideClick);
    }
  }

  onStatusChange(updatedVoid: boolean = false, calDriftAcknowledged: boolean = false): void {
    if (!updatedVoid && !calDriftAcknowledged && this.shouldWarnCalDrift(this.sampleStatus)) {
      this.showCalDriftWarning();
      return;
    }
    if (this.sampleStatus === SampleStatusEnum.Void && !updatedVoid) {
      this.voidSampleDialog();
      return;
    }
    this.sampleService.update({ ...this.rawSampleData, sampleStatus: this.sampleStatus, voidReason: this.sampleDetails?.voidReason }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (sample: Sample) => {
        const dimensions = sample.ihLabResults.find(result => result?.wipeDimensions)?.wipeDimensions;
        this.rawSampleData = { ...sample, wipeDimensions: dimensions };
        this.sampleDetails.sampleStatus = this.sampleStatus;
        this.toastService.showSuccess('Sample status updated successfully', true);
      },
      error: (error) => {
        this.sampleStatus = this.sampleDetails.sampleStatus;
        this.toastService.showError(error);
      }
    });
  }

  downloadAttachment(attachment: FileAttachment) {
    if (isExternalFileUrl(attachment.url)) {
      const a = document.createElement('a');
      a.href = attachment.url;
      a.download = (attachment.name || 'download').replace(/[\/\\]/g, '_').substring(0, 255);
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      a.click();
    } else {
      this.toastService.displayErrorMessage('No URL available for download', true)
    }
  }

  openReportGenerationPopup() {
    if (!this.sampleDetails) {
      this.toastService.displayErrorMessage('Sample data not loaded', true);
      return;
    }

    this.showReportGenerationPopup = true;
  }

  closeReportGenerationPopup() {
    this.showReportGenerationPopup = false;
  }

  async onPdfGenerated(event: PdfGeneratedData) {
    const organizationName = this.organizationsList.find(org => org.id === this.sampleDetails.organizationLevelId)?.name || '';

    try {
      await this.reportGenerationService.generateAndSaveReport(event, {
        entityIds: [event.entityId],
        entityType: event.entityType,
        organizationLevelId: this.sampleDetails.organizationLevelId,
        organizationLevelName: organizationName,
        metadataKey: RolePermissionObjects.Sample
      }, event.openInNewTab ?? true);
    } catch (error) {
      this.toastService.showError(error);
    } finally {
      // Refresh report history after generation
      this.loadSampleReports(this.sampleDetails.id);
    }
  }

  loadSampleReports(sampleId: string) {
    this.reportsSubscription?.unsubscribe();
    this.loadingReports = true;
    this.sampleReports = [];
    // DASH-2758: guard against a missing/empty route param so we never issue a doomed
    // GetBySample request with no id; render as no reports instead.
    if (!sampleId) {
      this.visibleReports = this.getVisibleReports(this.sampleReports);
      this.loadingReports = false;
      this.changeDetectorRef.detectChanges();
      return;
    }
    this.reportsSubscription = this.reportService.getBySample(sampleId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (reports) => {
        this.sampleReports = reports ?? [];
        this.visibleReports = this.getVisibleReports(this.sampleReports);
        this.loadingReports = false;
        this.changeDetectorRef.detectChanges();
      }
      // DASH-2758: read-path errors are caught in ReportService.getBySample (toast + empty []),
      // per services.md. The service emits [] via next: on failure, which resets loadingReports.
    });
  }

  /** True when the current user can read PII (GLOBAL_Pii Read). Employee Letters are PII-gated on the FE (BE enforces). */
  get canViewPii(): boolean {
    return this.permissionService.hasPermissionFlag(
      RolePermissionObjects.Pii,
      PermissionFlags.ReadMy | PermissionFlags.ReadHierarchy
    );
  }

  private isEmployeeLetter(report: Report): boolean {
    return report.type === ENTITY_TYPES.EMPLOYEE_LETTER
      || (report.fileAttachments?.some(fa => fa.documentType === DocumentType.Letters) ?? false);
  }

  /** Suppress Employee Letter reports for users without PII read; other report chips stay visible. */
  private getVisibleReports(reports: Report[]): Report[] {
    if (this.canViewPii) return reports;
    return reports.filter(report => !this.isEmployeeLetter(report));
  }

  openReport(report: Report) {
    if (!this.canViewPii && this.isEmployeeLetter(report)) {
      return;
    }
    const attachments = this.getValidAttachments(report.fileAttachments);
    if (attachments.length) {
      this.openFirstAttachment(attachments, 'Invalid letter URL');
    } else if (report.reportUrl) {
      if (!openFileInNewTab(report.reportUrl)) {
        this.toastService.displayErrorMessage('Invalid letter URL', true);
      }
    } else {
      this.toastService.displayErrorMessage('Letter URL not found', true);
    }
  }

  private getValidAttachments(attachments?: FileAttachment[]): FileAttachment[] {
    return attachments?.filter((attachment) => !!attachment.url) ?? [];
  }

  private openFirstAttachment(attachments: FileAttachment[], invalidUrlMessage: string): void {
    if (!openFileInNewTab(attachments[0].url)) {
      this.toastService.displayErrorMessage(invalidUrlMessage, true);
      return;
    }

    if (attachments.length > 1) {
      this.toastService.showInfo(
        'Opened first attachment. {{count}} more available.',
        true,
        { count: attachments.length - 1 }
      );
    }
  }

  viewCertificate() {
    if (!this.sampleDetails.deviceCalibrationHistoryId) return;
    this.deviceCalibrationHistoryService.getById(this.sampleDetails.deviceCalibrationHistoryId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        if (!openFileInNewTab(res.certificateUrl)) {
          this.toastService.displayErrorMessage('Certificate URL not found', true);
        }
      },
      error: (error) => {
        this.toastService.showError(error);
      }
    });
  }

  get sampleStatusName() {
    return this.sampleStatusList.find(status => status.id === this.sampleStatus)?.name;
  }

  get canGenerateReport(): boolean {
    if (!this.sampleDetails) return false;
    return this.sampleDetails.sampleStatus === SampleStatusEnum.Approved ||
           this.sampleDetails.sampleStatus === SampleStatusEnum.Void;
  }

  get heroSampleDate(): string {
    if (!this.sampleDetails?.startDate) return '';
    return this.dateFormatService.convertUTCDateStringToUserTimeZoneString(
      this.sampleDetails.startDate as string, this.dateFormatService.userDateFormat
    );
  }

  get heroRunTime(): string {
    if (!this.sampleDetails?.runTime) return '';
    return `${this.valueConversionService.convertSecondsToMinutes(this.sampleDetails.runTime).toFixed(0)} min`;
  }

  get sampleTypeName(): string {
    return this.sampleDetails?.sampleType != null ? getNamedEnum(SampleTypeEnum)[this.sampleDetails.sampleType] : '';
  }

  get sampleCategoryName(): string {
    return this.sampleDetails?.sampleCategory != null ? getNamedEnum(SampleCategoryEnum)[this.sampleDetails.sampleCategory] : '';
  }

  /** True when at least one lab result has actual gravimetric data (twa, concValue, or totalMass) */
  get hasGravimetricResults(): boolean {
    if (!this.sampleDetails?.ihLabResults?.length) return false;
    return this.sampleDetails.ihLabResults.some(r => r.twa != null || r.concValue != null || r.totalMass != null);
  }

  /** True when the sample status is non-editable (Approved, ApprovedLetterSent, Void, VoidLetterSent) */
  get isNonEditableStatus(): boolean {
    if (!this.sampleDetails) return true;
    return [
      SampleStatusEnum.Approved,
      SampleStatusEnum.ApprovedLetterSent,
      SampleStatusEnum.Void,
      SampleStatusEnum.VoidLetterSent
    ].includes(this.sampleDetails.sampleStatus);
  }

  /** True when the sample is in an approved state (Approved or ApprovedLetterSent) */
  get isApprovedStatus(): boolean {
    return this.sampleDetails?.sampleStatus === SampleStatusEnum.Approved
      || this.sampleDetails?.sampleStatus === SampleStatusEnum.ApprovedLetterSent;
  }

  get approvedByName(): string {
    return this.sampleDetails?.approvedByUserName ?? '';
  }

  get approvedOnDisplay(): string {
    if (!this.sampleDetails?.approvedDate) return '';
    return this.dateFormatService.convertUTCDateStringToUserTimeZoneString(
      this.sampleDetails.approvedDate as string, this.dateFormatService.userDateWithTimeFormat
    );
  }

  /** Un-approve is only offered to users holding the Sample UpdateHierarchy permission */
  get canUnapprove(): boolean {
    return this.isApprovedStatus
      && this.permissionService.hasPermissionFlag(this.permissionObject, PermissionFlags.UpdateHierarchy);
  }

  unApproveSample(): void {
    if (!this.sampleDetails) return;
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.getDynamicTranslation('Un-approve'),
        message: this.translateService.getDynamicTranslation('Un-approving this sample will re-open it for editing and clear the approval details. Do you want to continue?'),
        primaryButtonText: this.translateService.getDynamicTranslation('Yes'),
        secondaryButtonText: this.translateService.getDynamicTranslation('Cancel')
      }
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((confirmed: boolean) => {
      if (!confirmed) return;
      this.sampleService.update({ ...this.rawSampleData, sampleStatus: SampleStatusEnum.AwaitingApproval })
        .pipe(takeUntil(this.destroy$)).subscribe({
          next: () => {
            this.toastService.showSuccess(this.translateService.getDynamicTranslation('Sample un-approved successfully'), true);
            this.getSampleDetails(this.sampleDetails.id);
          },
          error: (error) => this.toastService.showError(error)
        });
    });
  }

  hasValue(val: string | number | null | undefined): boolean {
    return val != null && val !== '' && val !== '-';
  }

  hasVisibleFields(fields: KeyValue[]): boolean {
    if (!fields?.length) return false;
    return fields.some(f => (f.isShown == undefined || f.isShown) && this.hasValue(f.value));
  }

  get canChangeStatus(): boolean {
    return this.permissionService.hasPermissionFlag(
      this.permissionObject,
      gridToPermissionFlags[GridPermissions.Update]
    );
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.eventService.unsubscribeAllEvents([EventKeys.flyoutClose]);
  }

  public trackById = trackById;
  public trackByIndex = trackByIndex;
}
